import { Link, useSearch } from '@tanstack/react-router';
import {
  FilePenLineIcon,
  FilePlusIcon,
  FileSymlinkIcon,
  FileXIcon,
} from 'lucide-react';

import { fileChangeLink } from '@/features/workspace-overview';
import { useWorkspaceFilePreviewLink } from '@/features/workspace-files';
import type {
  FileChangeEventType,
  TaskFileChangeView,
} from '@/lib/api/generated/model';

/** The change, as a glyph before the word — scanned faster than read. */
const EVENT_ICON: Record<FileChangeEventType, typeof FilePenLineIcon> = {
  created: FilePlusIcon,
  modified: FilePenLineIcon,
  deleted: FileXIcon,
  moved: FileSymlinkIcon,
};
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { latestPerPath } from '../resultFiles';

/**
 * The files a run changed, each one click from a look at it (FR-014).
 *
 * The link is the Overview's own `fileChangeLink`, so a row here and a row on
 * the recent-changes list open the same place. What that place is comes from
 * `useWorkspaceFilePreviewLink` and is handed to `fileChangeLink` as an
 * argument (spec 140 FR-050, FR-053): `links.ts` is a plain module and cannot
 * read the `sm` breakpoint the decision depends on, so the deciding component
 * passes it in. These rows are on the task sheet, so the panel opens beside
 * the sheet and the task someone is reading stays where it is.
 *
 * A deleted file is named and not linked — a link that opens onto nothing
 * disappoints whoever clicks it, and there is no file to preview either, which
 * is why `fileChangeLink` never reaches the passed-in decision for one. A
 * moved file names where it came from and links where it now is.
 *
 * Nothing when the list is empty (FR-017).
 */
export function ResultFiles({
  workspaceId,
  files,
}: {
  workspaceId: string;
  files: readonly TaskFileChangeView[];
}) {
  /*
    The file already previewed beside this task, from the address. Read here
    rather than threaded as a prop: these rows render from four places inside
    the results tab (`ResultRunGroup`, "Other changes", `AlsoChangedFiles`) and
    the only thing the param decides is push versus replace. `strict: false`
    because the results tab renders on the board, the Tasks page and the run
    page alike.
  */
  const search: { preview?: unknown } = useSearch({ strict: false });
  const previewPath =
    typeof search.preview === 'string' ? search.preview : undefined;
  const previewLink = useWorkspaceFilePreviewLink(workspaceId, previewPath);
  const rows = latestPerPath(files);
  const phone = usePhoneWidth();
  if (rows.length === 0) return null;
  return (
    <ul aria-label="Files" className="flex flex-col gap-1 text-sm">
      {rows.map((file) => {
        const Icon = EVENT_ICON[file.event_type];
        return (
          <li
            key={file.path}
            className="flex min-w-0 flex-wrap items-baseline gap-x-2 max-md:flex-nowrap max-md:items-start"
          >
            <Icon
              aria-hidden="true"
              className="text-muted-foreground size-3.5 shrink-0 self-center max-md:mt-1 max-md:self-start"
            />
            {phone &&
            file.event_type !== 'deleted' &&
            !(file.event_type === 'moved' && file.source_path) ? (
              // On a phone the name leads and the folder goes under it
              // (spec 270): a whole path broke mid-word across two lines,
              // with the part a reader looks for at the very end. What
              // happened comes before the folder, so the ellipsis takes the
              // folder's tail and never the word.
              <span className="flex min-w-0 flex-1 flex-col">
                <Link
                  {...fileChangeLink(workspaceId, file, previewLink)}
                  aria-label={file.path}
                  className="text-primary font-mono [overflow-wrap:anywhere] underline underline-offset-2"
                >
                  {fileName(file.path)}
                </Link>
                <span className="text-muted-foreground truncate text-xs">
                  {[file.event_type, folderOf(file.path)]
                    .filter((part) => part !== '')
                    .join(' · ')}
                </span>
              </span>
            ) : file.event_type === 'deleted' ? (
              <>
                <span className="text-muted-foreground font-mono break-all line-through">
                  {file.path}
                </span>
                <span className="text-muted-foreground text-xs">deleted</span>
              </>
            ) : (
              <>
                {file.event_type === 'moved' && file.source_path ? (
                  <>
                    <span className="text-muted-foreground font-mono break-all">
                      {file.source_path}
                    </span>
                    <span aria-hidden="true" className="text-muted-foreground">
                      →
                    </span>
                  </>
                ) : null}
                <Link
                  {...fileChangeLink(workspaceId, file, previewLink)}
                  className="text-primary font-mono break-all underline underline-offset-2"
                >
                  {file.path}
                </Link>
                <span className="text-muted-foreground text-xs">
                  {file.event_type}
                </span>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The last segment of a workspace path: what a reader is looking for. */
function fileName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

/** Everything before the last segment, or '' for a file at the root. */
function folderOf(path: string): string {
  const cut = path.lastIndexOf('/');
  return cut < 0 ? '' : path.slice(0, cut);
}
